"use client";

import { proofMoments } from "./checkrideV6DemoData";

/**
 * The capability sections. The CAPTIONS here are copy; the QUOTES are verbatim exchanges
 * from real exams, pulled through the data file so the guard test covers them — including
 * that each pair really was adjacent in the record.
 */
const CAPTIONS: Record<string, { eyebrow: string; head: string; blurb: string; tag: string }> = {
  vague: {
    eyebrow: "It won't let you wave at an answer",
    head: "“That kind of thing” doesn’t pass",
    blurb:
      "Everyone has a few answers they can half-say. An examiner hears the hedge and stops on it — and so does this. Watch it refuse a sentence most apps would have marked correct.",
    tag: "catches a vague answer",
  },
  annual: {
    eyebrow: "It read your paperwork",
    head: "It checked the date on the annual",
    blurb:
      "This is the one that surprises people. The candidate glanced at the logbook and said the inspection had months left. It didn’t — and the examiner had actually done the arithmetic on the date in front of it.",
    tag: "caught a date error",
  },
  carb: {
    eyebrow: "It knows your aeroplane",
    head: "Right answer. Wrong aeroplane.",
    blurb:
      "Carburettor ice is a perfectly good hazard to bring up — in a carburetted engine. The aircraft in this scenario is fuel-injected, and the examiner said so. Generic answers don’t survive a specific aeroplane.",
    tag: "scenario-specific",
  },
  sfra: {
    eyebrow: "It corrects you, then makes you prove it",
    head: "Two corrections, and a harder question",
    blurb:
      "Being told you’re wrong is useless on its own. Notice what happens after the correction: it immediately asks the thing that shows whether you actually understood it, or just nodded.",
    tag: "corrects and re-tests",
  },
  attitudes: {
    eyebrow: "It knows close from correct",
    head: "Nearly right is its own answer",
    blurb:
      "The candidate named a real hazardous attitude — just not the one driving this decision. Close enough to feel right, not close enough to pass. That distinction is most of what the oral is.",
    tag: "close, not correct",
  },
  oneshort: {
    eyebrow: "It remembers what you said an hour ago",
    head: "“You’re one short”",
    blurb:
      "This is the one that unsettles people. The candidate listed the gyroscopic instruments and missed one — and the examiner caught it using something he had said much earlier, about a different failure, in a different part of the flight.",
    tag: "used his own earlier answer",
  },
  halfright: {
    eyebrow: "Half an answer is not an answer",
    head: "“The other half”",
    blurb:
      "He got the climb case right and stopped there. Most of the time that reads as correct. Here it reads as half, and the examiner says which half is missing before moving on.",
    tag: "catches what you skipped",
  },
  placard: {
    eyebrow: "It wants the detail, not the gist",
    head: "Where it goes matters",
    blurb:
      "“Somewhere the pilot can see it” is the sort of answer that feels complete until someone asks exactly where. The regulation is specific, and so is the follow-up.",
    tag: "pushes for precision",
  },
};

//: Ordered so the two that land hardest — knowing this aeroplane, and remembering what
//: he said an hour ago — are not both at the end.
const ORDER = ["vague", "annual", "carb", "oneshort", "sfra", "halfright",
               "attitudes", "placard"];

export default function V6Proof() {
  const moments = ORDER.map((id) => proofMoments.find((m) => m.id === id)).filter(
    (m): m is NonNullable<typeof m> => Boolean(m),
  );

  return (
    <>
      {moments.map((m, i) => {
        const c = CAPTIONS[m.id];
        const alt = i % 2 === 1;
        return (
          <section
            key={m.id}
            id={i === 0 ? "vague" : m.id}
            className={`py-20 md:py-28 px-6 border-t border-hairline/60 ${
              alt ? "bg-white" : "bg-ground-alt"
            }`}
          >
            <div className="max-w-3xl mx-auto">
              <span className="text-[11px] font-semibold tracking-wider uppercase text-accent">
                {c.eyebrow}
              </span>
              <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-[-0.02em] text-ink">
                {c.head}
              </h2>
              <p className="mt-4 text-[17px] leading-relaxed text-ink-soft max-w-2xl">
                {c.blurb}
              </p>

              <div className="mt-9 rounded-2xl border border-hairline overflow-hidden lift bg-white">
                <div className="px-5 py-4 bg-ground-alt">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink-faint mb-1.5">
                    The candidate said
                  </div>
                  <p className="text-[15px] leading-relaxed text-ink-soft whitespace-pre-wrap">
                    {m.applicant}
                  </p>
                </div>
                <div className="px-5 py-4 border-t border-hairline">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-accent">
                      The examiner came back with
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-accent/5 text-accent text-[10px] font-semibold tracking-wide">
                      {c.tag}
                    </span>
                  </div>
                  <p className="text-[15px] leading-relaxed text-ink whitespace-pre-wrap">
                    {m.examiner}
                  </p>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}
